// src/utils/assetuploadform/processAssetForm.js
// -----------------------------------------------------------------------------
// processAssetForm – “dry-run” normalization and validation for the
// Asset Upload Form. This mirrors the field-processing path in
// FormActionsContext (dropdown mapping, transformations, calculated
// fields, required-checks), but **does not** execute any submission
// actions and **does not** write to formSubmissions.
//
// Usage:
//   const result = await processAssetForm({
//     formId,
//     submissionData,    // [{ id, ufi, type, label, value, required }, …] (optional)
//     layoutConfig,      // if submissionData missing, we’ll construct from layout+changes
//     fieldsMetadata,    // map of fieldId -> metadata (must include .ufi)
//     dropdownOptions,   // map of optionsSetUFI -> [{ optionId, label, …}, …]
//     fieldChanges,      // map of fieldId -> { value, … } (optional, used to construct values)
//     validationErrors,  // map of fieldId -> { errorMessage, externalHelperText } (optional)
//   });
//
// Returns:
//   {
//     submissionId,            // uuid
//     submissionData,          // final normalized array of fields
//     flattenedSubmission,     // { [ufi]: value, label_ufi: 'Label' }
//     nestedResponses,         // { [ufi]: { value }, [label_ufi]: 'Label' }
//     isValid,                 // boolean
//     errors                   // [{ fieldId, label, message }]
//   }
//
// Notes:
//   • This file intentionally keeps logs and comments verbose to match the
//     style in FormActionsContext and to make debugging easy.
//   • No UI context or React hooks are used here – this is a pure utility.
//   • Keep this in sync with any future changes made in FormActionsContext,
//     especially around dropdown processing, transformations and calculated
//     fields.
//
// -----------------------------------------------------------------------------

import { v4 as uuidv4 } from 'uuid';
import { evaluateAllCalculatedFields } from '../../utils/calculatedFieldUtils';
import { applyTransformations } from '../../utils/transformationUtils';
import { FIELD_TYPE_CALCULATED } from '../../components/Fields/constants/fieldTypes';

/**
 * Local helper – shallow "normalization" of fieldsMetadata input.
 * Mirrors the logic used in FormActionsContext.getNormalizedFieldsMetadata().
 */
const normalizeFieldsMetadata = (raw) => {
//  console.log("[processAssetForm] Raw fieldsMetadata:", raw);
  const normalized = raw?.value || raw || {};
//  console.log("[processAssetForm] Normalized fieldsMetadata:", normalized);
  return normalized;
};

/**
 * Local helper – filter out submit buttons from an array of fields.
 * Mirrors FormActionsContext.filterOutSubmitButton().
 */
const filterOutSubmitButton = (data = []) => {
  return data.filter((field) => field.type !== 'submit');
};

/**
 * Local helper – dropdown mapping identical to FormActionsContext:
 * Converts single-select / multi-select optionId(s) → their label(s),
 * applying transformations when present in field metadata.
 */
const transformDropdownSubmissionValues = (submissionData, fieldsMetadata, dropdownOptions) => {
  console.log("[processAssetForm] Transforming dropdown values before submission.");

  return submissionData.map((field) => {
    const fieldMetadata = fieldsMetadata[field.id];
    if (!fieldMetadata) {
      console.warn(`[processAssetForm] No metadata found for field "${field.id}". Skipping.`);
      return field;
    }

    const isDropdown =
      fieldMetadata.type === "single-select" || fieldMetadata.type === "multi-select";
    const optionsSetUFI = fieldMetadata.optionsSetUFI; // tells us which options set to use

    if (isDropdown && optionsSetUFI && dropdownOptions?.[optionsSetUFI]) {
      const options = dropdownOptions[optionsSetUFI]; // from MultiAnswerFieldContext in app

      // Multi-select → map array of optionIds → transformed labels
      if (Array.isArray(field.value)) {
        const updatedValues = field.value.map((optionId) => {
          const matchedOption = options.find((opt) => opt.optionId === optionId);
          const transformedLabel = matchedOption
            ? applyTransformations(matchedOption.label, fieldMetadata.transformations)
            : optionId;

//          console.log(`[processAssetForm] Multi-select field "${field.id}" transformation:`, {
//            original: matchedOption?.label,
//            transformed: transformedLabel
//          });

          return transformedLabel;
        });

        return { ...field, value: updatedValues };
      } else {
        // Single-select → replace optionId with transformed label
        const matchedOption = options.find((opt) => opt.optionId === field.value);
        const transformedValue = matchedOption
          ? applyTransformations(matchedOption.label, fieldMetadata.transformations)
          : field.value;

//        console.log(`[processAssetForm] Single-select field "${field.id}" transformation:`, {
//          original: matchedOption?.label,
//          transformed: transformedValue
//        });

        return { ...field, value: transformedValue };
      }
    }

    return field;
  });
};

/**
 * Local helper – construct submissionData when caller didn’t supply it,
 * using layoutConfig + fieldChanges + fieldsMetadata. Matches the construction
 * pattern in FormActionsContext when submissionData is missing.
 */
const buildSubmissionDataFromLayout = (layoutConfig, fieldsMetadata, fieldChanges = {}) => {
  if (!layoutConfig || !fieldsMetadata) return [];

  const rows = Object.keys(layoutConfig);
  const items = rows.flatMap((rowKey) =>
    (layoutConfig[rowKey] || []).map((field) => ({
      id: field.id,
      ufi: fieldsMetadata[field.id]?.ufi || field.id,
      type: fieldsMetadata[field.id]?.type || field.type, // prefer metadata type
      label: fieldsMetadata[field.id]?.label || field.label || field.id,
      value: fieldChanges[field.id]?.value ?? fieldsMetadata[field.id]?.value ?? '',
      required: fieldsMetadata[field.id]?.required || field.required || false,
    }))
  );

  return filterOutSubmitButton(items);
};

/**
 * Core function: processAssetForm
 */
export async function processAssetForm({
  formId,
  submissionData,         // optional array of { id, ufi, type, label, value, required }
  layoutConfig,           // used only if submissionData is missing
  fieldsMetadata,         // map of fieldId -> metadata
  dropdownOptions,        // map of optionsSetUFI -> options[]
  fieldChanges = {},      // optional changes map from builder
  validationErrors = {},  // optional pre-existing per-field errors (from FieldEditContext)
} = {}) {
  // 0) Guards
  if (!formId) {
    console.error("[processAssetForm] Missing formId. Aborting.");
    return {
      submissionId: null,
      submissionData: [],
      flattenedSubmission: {},
      nestedResponses: {},
      isValid: false,
      errors: [{ fieldId: 'form', label: 'Form', message: 'Missing formId.' }],
    };
  }

  const normalizedFieldsMetadata = normalizeFieldsMetadata(fieldsMetadata);
  if (!Object.keys(normalizedFieldsMetadata).length) {
    console.error("[processAssetForm] fieldsMetadata is still not available. Aborting submission.");
    return {
      submissionId: null,
      submissionData: [],
      flattenedSubmission: {},
      nestedResponses: {},
      isValid: false,
      errors: [{ fieldId: 'metadata', label: 'Metadata', message: 'Form metadata is not loaded. Please try again.' }],
    };
  }

//  console.log("[processAssetForm] fieldsMetadata loaded successfully:", normalizedFieldsMetadata);

  // 1) Normalize incoming submission data. If missing, build from layout+changes.
  let working = Array.isArray(submissionData) && submissionData.length
    ? submissionData.slice()
    : buildSubmissionDataFromLayout(layoutConfig, normalizedFieldsMetadata, fieldChanges);

  working = filterOutSubmitButton(working);
  if (!working.length) {
    console.error("[processAssetForm] No valid submission data found after filtering.");
    return {
      submissionId: null,
      submissionData: [],
      flattenedSubmission: {},
      nestedResponses: {},
      isValid: false,
      errors: [{ fieldId: 'data', label: 'Data', message: 'No valid submission data found.' }],
    };
  }

  // 2) Generate a unique submissionId
  const submissionId = uuidv4();
  console.log(`[processAssetForm] Generated submissionId: ${submissionId}`);

  // 3) Convert UFI to fieldId (mirror logic in FormActionsContext)
  working = working.map((field) => ({
    ...field,
    id:
      Object.values(normalizedFieldsMetadata).find((meta) => meta.ufi === field.ufi)?.id ||
      field.id,
  }));

  // 4) IMPORTANT: For the Asset Upload Form (AUF), keep dropdown answers as
  //    the canonical optionId(s). We no longer replace optionIds with labels here.
  //    Labels will be resolved later (e.g., for tags) using dropdownOptions +
  //    fieldsMetadata. This keeps AUF answers stable even if labels/transformations
  //    change over time.
  //
  //    🔴 DO NOT call transformDropdownSubmissionValues() here anymore.
  //         – working[].value stays as:
  //             • single-select: optionId
  //             • multi-select:  [optionId,...]
  //             • text/date/etc: raw string
  //
  // 5) Apply encode transformations ONLY to non-dropdown fields.
  working = working.map((field) => {
    const fieldMeta = normalizedFieldsMetadata[field.id];
    const isDropdown =
      fieldMeta?.type === 'single-select' ||
      fieldMeta?.type === 'multi-select';

    // Skip encode on dropdowns (we want optionIds to remain canonical)
    if (!fieldMeta?.transformations?.encode || isDropdown) {
      return field;
    }

//    console.log(`[processAssetForm] Encoding field "${field.id}" (label: "${field.label}") because encode=true in metadata.`);
    if (Array.isArray(field.value)) {
      return {
        ...field,
        value: field.value.map((v) =>
          v == null ? '' : encodeURIComponent(String(v))
        ),
      };
    }

    if (field.value && typeof field.value === 'object') {
      // If you ever encode structured values, define a policy; for now leave as-is
      return field;
    }

    return {
      ...field,
      value: encodeURIComponent(field.value || ''),
    };
  });


  // 6) Calculated fields – same pattern as FormActionsContext
  //    Build { ufi: value } map of current answers, evaluate ALL calc fields,
  //    and push each result into working if not already present.
  const answersByUfi = working.reduce((acc, f) => {
    acc[f.ufi] = f.value;
    return acc;
  }, {});

  const calcResults = evaluateAllCalculatedFields(
    normalizedFieldsMetadata,
    answersByUfi
  );

  Object.entries(calcResults).forEach(([ufi, value]) => {
    // find the metadata for this calculated field
    const meta = Object.values(normalizedFieldsMetadata).find(
      (m) => m.ufi === ufi && m.type === FIELD_TYPE_CALCULATED
    );
    if (!meta) return; // safety

    // avoid duplicates if someone already injected this field
    if (working.some((f) => f.ufi === ufi)) return;

    working.push({
      id:   meta.id,
      ufi,                                // calculated UFI
      label: meta.label || meta.name || 'Calculated',
      type: FIELD_TYPE_CALCULATED,
      value,
      required: false,                    // never required
    });

    console.log(
      `[processAssetForm] Added calculated field "${meta.label}" (ufi: ${ufi}) with value:`,
      value
    );
  });

  // 7) Required validation – mirror FormActionsContext’s checks.
  //    We cannot call updateValidationError here; we collect `errors` to return.
  let isValid = true;
  const errors = [];

  if (!validationErrors || typeof validationErrors !== 'object') {
    console.error("[processAssetForm] validationErrors is undefined or not an object. Proceeding with local checks only.");
  }

  working.forEach((field) => {
    const required =
      normalizedFieldsMetadata[field.id]?.required ||
      field.required ||
      false;

    // ENTERPRISE: type-aware required check (supports 0/false/object)
    const v = field.value;
    const hasValue =
      Array.isArray(v) ? v.length > 0
      : (v === null || v === undefined) ? false
      : (typeof v === 'string') ? v.trim() !== ''
      : true;

    const hasExternalError = validationErrors?.[field.id];

    if ((required && !hasValue) || hasExternalError) {
      const msg = hasExternalError?.errorMessage ||
        `The field "${field.label}" is required or has errors.`;
      console.warn(`[processAssetForm] Validation failed for field: ${field.label} (ID: ${field.id})`);

      errors.push({
        fieldId: field.id,
        label: field.label,
        message: msg,
      });
      isValid = false;
    } else {
      // pass
//      console.log(`[processAssetForm] Validation passed for field: ${field.label} (ID: ${field.id})`);
    }
  });

  if (!isValid) {
    console.error("[processAssetForm] Form is not valid and cannot be submitted.");
  } else {
    // mirror the FormActionsContext ‘setIsFormValid(true)’ effect conceptually
//    console.log("[processAssetForm] Form is valid after normalization.");
  }

  // 8) Build both shapes: flattenedSubmission + nestedResponses
  //    Matches the structure used in FormSubmissionsContext.
  const flattenedSubmission = {};
  const nestedResponses = {};

  working.forEach((f) => {
    const labelKey = `label_${f.ufi}`;
    const humanLabel = f.label || "Unknown Field";

    flattenedSubmission[f.ufi] = f.value;
    flattenedSubmission[labelKey] = humanLabel;

    nestedResponses[f.ufi] = { value: f.value };
    nestedResponses[labelKey] = humanLabel;
  });

  // 9) Return everything the caller needs to continue with the DAM flow.
  //    Do NOT execute any submission actions or write to formSubmissions here.
  return {
    submissionId,
    submissionData: working,     // final normalized fields
    flattenedSubmission,
    nestedResponses,
    isValid,
    errors,
  };
}
